import { useCallback, useEffect, useRef, useState } from 'react';
import { API, authFetch, getAuth } from '../api';

/**
 * The four statuses POST /models/init can return.
 *
 *   NOTHING  — nothing to run; reuse cached output, no banner
 *   STARTED  — backend just kicked the model off
 *   RUNNING  — backend was already mid-run when we asked
 *   FAILED   — error during init; show a retry affordance
 */

/** How often we poll while STARTED/RUNNING. Model
 *  run takes a few minutes or less, so 5s gives ~12-60 polls per run. */
const POLL_INTERVAL_MS = 5000;

/**
 * Drives the model init handshake on Dashboard / Graphs / Insights.
 *
 * Usage:
 *   const { status, phase, refresh } = useModelStatus();
 *
 * The hook fires init on mount, then keeps polling /models/init every
 * POLL_INTERVAL_MS while the result is STARTED or RUNNING. Polling
 * stops the moment we see NOTHING (no run needed → cached output is
 * fresh) or FAILED (user has to retry).
 */
export function useModelStatus() {
  const [status, setStatus] = useState(null);
  // 'idle' | 'loading' | 'ready' | 'error'
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);

  // Bumped by refresh() to re-trigger init from a Retry tap.
  const [tick, setTick] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const auth = getAuth();
    if (!auth) {
      // No token yet — UI shouldn't even be on this page, but bail
      // safely just in case (e.g. landing page rendering Dashboard
      // briefly before the route guard kicks in).
      setStatus(null);
      setPhase('idle');
      return;
    }

    let cancelled = false;
    let timer = null;

    const callInit = async () => {
      if (cancelled || !mountedRef.current) return;

      setPhase((current) => (current === 'idle' ? 'loading' : current));

      try {
        const res = await authFetch(API.modelInit(), { method: 'POST' });

        if (cancelled || !mountedRef.current) return;

        if (!res.ok) {
          setStatus('FAILED');
          setError(`Init failed (${res.status})`);
          setPhase('error');
          return;
        }

        const json = await res.json();
        if (cancelled || !mountedRef.current) return;

        setStatus(json.status);
        setError(null);
        setPhase('ready');

        // Schedule the next poll only while a run is actively pending.
        if (json.status === 'STARTED' || json.status === 'RUNNING') {
          timer = setTimeout(callInit, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        setStatus('FAILED');
        setError(err instanceof Error ? err.message : 'Network error');
        setPhase('error');
      }
    };

    callInit();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tick]);

  return { status, phase, error, refresh };
}
